export interface Project {
  id: string;
  title: {
    en: string;
    de: string;
  };
  company: {
    en: string;
    de: string;
  };
  appType: 'web' | 'mobile' | 'cross-platform' | 'desktop';
  description: {
    en: string;
    de: string;
  };
  year: string;
  role: {
    en: string;
    de: string;
  };
  stack: string[];
  imageUrl?: string;
  summary: {
    en: string;
    de: string;
  };
  features: {
    en: string[];
    de: string[];
  };
  links?: {
    github?: string;
    demo?: string;
  };
  secondaryImages?: string[];
  codeSnippets?: {
    language: string;
    code: string;
    info?: {
      en: string;
      de: string;
    };
  }[];
  /** Outcome-first one-liner (not tech-first). */
  outcomeHeadline?: { en: string; de?: string };
  /** "What made it hard" (1–2 bullets). */
  hardParts?: { en: string[]; de?: string[] };
  /** "What I shipped" (max 3 bullets). */
  shipped?: { en: string[]; de?: string[] };
  /** Proof/signals (compliance, scale, reliability, standards). */
  proof?: { en: string[]; de?: string[] };
  /** Short grouped stack string. */
  stackGrouped?: { en: string; de?: string };
  /** Optional one-line niche tie-in. */
  nicheTieIn?: { en: string; de?: string };
  /** For filter chips. */
  tags?: string[];
}

/** Category IDs for filter chips; must match i18n keys projects.filters.* */
export const PROJECT_CATEGORY_IDS = [
  'all',
  'scientificTools',
  'platforms',
  'realTimeMobile',
  'dataVisualization',
  'openSource',
] as const;

export type ProjectCategoryId = (typeof PROJECT_CATEGORY_IDS)[number];

/** Returns categories for a project; uses tags when set, else derives from appType/company/stack. */
export function getProjectCategories(project: Project): string[] {
  if (project.tags && project.tags.length > 0) {
    return project.tags;
  }
  const categories: string[] = [];
  const stackLower = project.stack.map((s) => s.toLowerCase()).join(' ');
  const companyEn = project.company.en.toLowerCase();
  if (
    companyEn.includes('open source') ||
    stackLower.includes('open source') ||
    stackLower.includes('npm')
  ) {
    categories.push('Open Source');
  }
  if (
    project.appType === 'mobile' ||
    project.appType === 'cross-platform' ||
    stackLower.includes('react native')
  ) {
    if (
      stackLower.includes('pusher') ||
      stackLower.includes('agora') ||
      stackLower.includes('socket')
    ) {
      categories.push('Real-time Mobile');
    }
  }
  if (
    stackLower.includes('electron') ||
    stackLower.includes('plotly') ||
    stackLower.includes('grpc') ||
    companyEn.includes('speag')
  ) {
    categories.push('Scientific Tools');
  }
  if (stackLower.includes('plotly') || stackLower.includes('visualization')) {
    categories.push('Data Visualization');
  }
  if (
    project.appType === 'web' &&
    (stackLower.includes('qooxdoo') || stackLower.includes('redux'))
  ) {
    categories.push('Platforms');
  }
  return [...new Set(categories)];
}

export const projects: Project[] = [
  {
    id: 'my-nextstage',
    title: {
      en: 'My NextStage',
      de: 'My NextStage'
    },
    company: {
      en: 'Personal Product',
      de: 'Eigenes Produkt'
    },
    appType: 'web',
    description: {
      en: 'An AI-assisted platform for structured job application and interview preparation.',
      de: 'Eine KI-gestützte Plattform für strukturierte Bewerbungen und Interviewvorbereitung.'
    },
    year: '2026',
    role: {
      en: 'Product Engineer',
      de: 'Product Engineer'
    },
    stack: ['React', 'TypeScript', 'Express', 'Node.js', 'AI / LLM workflows', 'Vite'],
    imageUrl: '/assets/MyNextStage_01.png',
    summary: {
      en: 'My NextStage is an AI-assisted platform for structured job application and interview preparation. It helps users turn real experience into stronger applications, clearer interview preparation, and more confident career decisions.',
      de: 'My NextStage ist eine KI-gestützte Plattform für strukturierte Bewerbungen und Interviewvorbereitung. Sie hilft Nutzern, echte Erfahrung in stärkere Bewerbungen, klarere Interviewvorbereitung und selbstbewusstere Karriereentscheidungen zu verwandeln.'
    },
    outcomeHeadline: {
      en: 'An AI-assisted platform that turns real experience into stronger applications and clearer interview preparation.',
      de: 'Eine KI-gestützte Plattform, die echte Erfahrung in stärkere Bewerbungen und klarere Interviewvorbereitung verwandelt.'
    },
    hardParts: {
      en: [
        'Turning messy, personal career context into structured input that AI workflows can use reliably.',
        'Keeping AI-assisted output grounded, honest, and genuinely useful rather than generic.'
      ],
      de: [
        'Unstrukturierten, persönlichen Karrierekontext in strukturierte Eingaben zu überführen, die KI-Workflows zuverlässig nutzen können.',
        'KI-gestützte Ergebnisse fundiert, ehrlich und wirklich nützlich zu halten statt generisch.'
      ]
    },
    shipped: {
      en: [
        'React + TypeScript frontend with a low-friction workflow for capturing structured user context',
        'Express and Node.js backend orchestrating AI-assisted application and interview-preparation flows',
        'Product strategy, UX, and evaluation to keep AI suggestions grounded and useful'
      ],
      de: [
        'React- + TypeScript-Frontend mit einem reibungsarmen Ablauf zur Erfassung von strukturiertem Nutzerkontext',
        'Express- und Node.js-Backend, das KI-gestützte Bewerbungs- und Interviewvorbereitungs-Abläufe orchestriert',
        'Produktstrategie, UX und Evaluation, um KI-Vorschläge fundiert und nützlich zu halten'
      ]
    },
    proof: {
      en: [
        'Helps users turn real experience into stronger applications and more confident career decisions',
        'End-to-end ownership: product strategy, UX, frontend and backend implementation'
      ],
      de: [
        'Hilft Nutzern, echte Erfahrung in stärkere Bewerbungen und selbstbewusstere Karriereentscheidungen zu verwandeln',
        'End-to-end-Verantwortung: Produktstrategie, UX, Frontend- und Backend-Umsetzung'
      ]
    },
    stackGrouped: {
      en: 'React + TypeScript • Express / Node.js • AI-assisted workflows',
      de: 'React + TypeScript • Express / Node.js • KI-gestützte Workflows'
    },
    nicheTieIn: {
      en: 'Proof of the product engineer direction: an AI-powered product built end to end.',
      de: 'Beleg für die Product-Engineer-Richtung: ein KI-gestütztes Produkt, end-to-end gebaut.'
    },
    tags: ['Platforms'],
    features: {
      en: [
        'Structured capture of real experience and career context',
        'AI-assisted job application preparation',
        'AI-assisted interview preparation',
        'Guided workflows that reduce cognitive load',
        'Product strategy, UX, frontend and backend built end to end'
      ],
      de: [
        'Strukturierte Erfassung von echter Erfahrung und Karrierekontext',
        'KI-gestützte Vorbereitung von Bewerbungen',
        'KI-gestützte Interviewvorbereitung',
        'Geführte Abläufe, die die kognitive Last reduzieren',
        'Produktstrategie, UX, Frontend und Backend end-to-end umgesetzt'
      ]
    },
    links: {
      demo: 'https://www.mynextstage.net'
    },
    secondaryImages: [
      '/assets/MyNextStage_00.png',
      '/assets/MyNextStage_02.png']
  },
  {
    id: 'dak-r',
    title: {
      en: 'DAK-R',
      de: 'DAK-R'
    },
    company: {
      en: 'SPEAG',
      de: 'SPEAG'
    },
    appType: 'cross-platform',
    description: {
      en: 'Cutting-edge solution for non-destructive dielectric measurement of thin, low-loss substrates',
      de: 'Hochmoderne Lösung für zerstörungsfreie dielektrische Messung dünner, verlustarmer Substrate'
    },
    year: '2025',
    role: {
      en: 'Frontend Engineer',
      de: 'Frontend-Ingenieur'
    },
    stack: ['Electron', 'React', 'React Router', 'TypeScript', 'gRPC', 'Material-UI', 'Plotly.js', 'Axios', 'Webpack', 'ESLint & Prettier', 'Jest', 'Git'],
    imageUrl: '/assets/DAK-R_01.png',
    summary: {
      en: 'Lab users get a stable desktop tool to run VNA measurements, manage setup and calibration, and export results—with the UI talking to local hardware services over gRPC-web and staying in sync with measurement state.',
      de: 'Lab-Benutzer erhalten ein stabiles Desktop-Tool zur Durchführung von VNA-Messungen, zur Verwaltung von Setup und Kalibrierung sowie zum Export von Ergebnissen—die Oberfläche kommuniziert mit lokalen Hardwareservices über gRPC-web und bleibt mit dem Messzustand synchron.'
    },
    outcomeHeadline: {
      en: 'Stable desktop UI for lab-grade VNA measurements, setup, and export—driven by gRPC-web and domain state.',
      de: 'Stabile Desktop-UI für laborfähige VNA-Messungen, Setup und Export—gesteuert durch gRPC-web und Domänenzustand.'
    },
    hardParts: {
      en: [
        'Keeping UI and measurement state in sync across gRPC calls, cancellations, and nested loaded/unloaded measurement flows.'
      ],
      de: [
        'UI und Messzustand über gRPC-Aufrufe, Abbruch und verschachtelte geladene/entladene Messabläufe synchron zu halten.'
      ]
    },
    shipped: {
      en: [
        'Electron + React SPA with custom gRPC-web service layer (retries, protobuf framing)',
        'Domain Contexts for VNA connection, measurements, and setup flow',
        'MUI theming + Plotly charts for measurement data and export'
      ],
      de: [
        'Electron + React SPA mit eigener gRPC-web-Service-Schicht (Retries, Protobuf-Framing)',
        'Domain-Contexts für VNA-Verbindung, Messungen und Setup-Ablauf',
        'MUI-Theming + Plotly-Charts für Messdaten und Export'
      ]
    },
    proof: {
      en: [
        'Compliant with IPC test method TM-650 2.5.5.13',
        'High-precision measurements (Q-factor > 25000), multi-frequency (10–45 GHz)'
      ],
      de: [
        'Konform mit IPC-Testmethode TM-650 2.5.5.13',
        'Hochpräzise Messungen (Q-Faktor > 25000), Mehrfrequenz (10–45 GHz)'
      ]
    },
    stackGrouped: {
      en: 'Electron + React + TS • gRPC-web • MUI • Plotly • Jest',
      de: 'Electron + React + TS • gRPC-web • MUI • Plotly • Jest'
    },
    nicheTieIn: {
      en: 'Hardware-backed scientific UI with strict state and protocol handling.'
    },
    tags: ['Scientific Tools', 'Data Visualization'],
    features: {
      en: [
        'Multi-frequency measurements (10, 17, 26, 35, and 45 GHz) in a single instrument',
        'High-precision measurements with Q-factor > 25000',
        'Compliant with IPC test method TM-650 2.5.5.13',
        'Modern web-based intuitive graphical user interface',
        'Advanced data analysis tools with tolerance and uncertainty ranges',
        'High-speed VNA control and accurate dielectric parameter computation',
        'Supports measurements of εr: 1–100, tan δ: 0.00001 - 0.01'
      ],
      de: [
        'Mehrfrequenz-Messungen (10, 17, 26, 35 und 45 GHz) in einem einzigen Instrument',
        'Hochpräzise Messungen mit Q-Faktor > 25000',
        'Konform mit IPC-Testmethode TM-650 2.5.5.13',
        'Moderne webbasierte intuitive grafische Benutzeroberfläche',
        'Fortschrittliche Datenanalysetools mit Toleranz- und Unsicherheitsbereichen',
        'Hochgeschwindigkeits-VNA-Steuerung und präzise Berechnung dielektrischer Parameter',
        'Unterstützt Messungen von εr: 1–100, tan δ: 0.00001 - 0.01'
      ]
    },
    secondaryImages: [
      '/assets/DAK-R_02.png',
      '/assets/DAK-R_03.png'
    ],
    codeSnippets: [
      {
        language: 'typescript',
        code: `const cleanAfterCancelling = (
  setContextValue: React.Dispatch<React.SetStateAction<MeasurementContextValue>>
) => {
  setContextValue(prevValue => {
    const { [Product.DAK_R]: { loaded, unloaded } } = prevValue
    const nextDAKRValue: MeasurementContextValue[Product.DAK_R] = {}
    
    // Handle unloaded measurements
    if (unloaded) {
      Object.keys(unloaded).forEach(key => {
        const mKey = Number(key)
        if (unloaded[mKey].status === MeasurementStatus.FINISHED) {
          nextDAKRValue.unloaded = nextDAKRValue.unloaded || {}
          nextDAKRValue.unloaded[mKey] = unloaded[mKey]
        }
      })
    }
    
    // Handle loaded measurements
    if (loaded) {
      nextDAKRValue.loaded = {}
      const sampleIds = Object.keys(loaded)
      for (let i = 0; i < sampleIds.length; i++) {
        const sampleData = loaded[sampleIds[i]]
        const measurements = Object.entries(sampleData.measurement)
        if (measurements.some(([, m]) => m.status !== MeasurementStatus.FINISHED)) {
          // Clean up needed for unfinished measurements
          const cleanMeasurement: LoadedMeasurements[string] = {
            materialName: sampleData.materialName,
            sampleThickness: sampleData.sampleThickness,
            measurement: {}
          }
          for (let j = 0; j < measurements.length; j++) {
            const [freq, m] = measurements[j]
            if (m.status === MeasurementStatus.FINISHED) {
              cleanMeasurement.measurement[Number(freq)] = m
            }
          }
          nextDAKRValue.loaded[sampleIds[i]] = cleanMeasurement
        } else {
          nextDAKRValue.loaded[sampleIds[i]] = sampleData
        }
      }
    }
    
    return { ...prevValue, [Product.DAK_R]: nextDAKRValue }
  })
}`,
        info: {
          en: `This snippet shows part of the measurement cleanup logic used after a cancellation.

Why it matters:
• It preserves completed loaded and unloaded measurements.
• It removes unfinished measurement state without mutating the existing context.
• It keeps nested domain data type-safe in React and TypeScript.
• It protects the measurement workflow from showing partial or inconsistent results.

The interesting part is not the amount of code, but the care needed around state, cancellation, and domain-specific measurement data.`,
          de: `Dieser Ausschnitt zeigt einen Teil der Bereinigungslogik nach dem Abbruch einer Messung.

Warum das relevant ist:
• Abgeschlossene geladene und ungeladene Messungen bleiben erhalten.
• Unvollständiger Messzustand wird entfernt, ohne den bestehenden Context zu mutieren.
• Verschachtelte Domänendaten bleiben in React und TypeScript typsicher.
• Der Messablauf wird davor geschützt, teilweise oder inkonsistente Ergebnisse anzuzeigen.

Interessant ist hier weniger die Menge an Code, sondern die Sorgfalt rund um State, Abbruchverhalten und domänenspezifische Messdaten.`
        }
      }
    ]
  },
  {
    id: 'sim4life',
    title: {
      en: 'Sim4Life',
      de: 'Sim4Life'
    },
    company: {
      en: 'SPEAG',
      de: 'SPEAG'
    },
    appType: 'web',
    description: {
      en: 'Sim4Life is a platform for simulating biological systems.',
      de: 'Sim4Life ist eine Plattform für die Simulation biologischer Systeme.'
    },
    year: '2024',
    role: {
      en: 'Frontend Engineer',
      de: 'Frontend-Ingenieur'
    },
    stack: ['React', 'TypeScript', 'Redux', 'WebSockets', 'Material-UI', 'Webpack', 'Enzyme', 'Styled Components', 'Socket.IO Client', 'Plotly.js'],
    imageUrl: '/assets/Sim4Life_01.png',
    summary: {
      en: 'Simulation engineers get a desktop-like workspace in the browser: remote 3D/graphical view, live control of models, configuration panels, and real-time feedback—with tight integration to backend services via sockets and streaming.',
      de: 'Simulationsingenieure erhalten einen desktopähnlichen Arbeitsbereich im Browser: Remote-3D-/Grafikansicht, Live-Steuerung von Modellen, Konfigurationspanels und Echtzeit-Feedback—mit enger Anbindung an Backend-Services über Sockets und Streaming.'
    },
    outcomeHeadline: {
      en: 'Browser-based workspace for remote simulation control and real-time visualization—sockets, streaming, and Plotly at scale.',
      de: 'Browserbasierter Arbeitsbereich für Remote-Simulationssteuerung und Echtzeit-Visualisierung—Sockets, Streaming und Plotly im Maßstab.'
    },
    hardParts: {
      en: [
        'Keeping heavy Plotly plots performant (65k+ points) and theming them to MUI while handling real-time updates.'
      ],
      de: [
        'Schwere Plotly-Plots performant zu halten (65k+ Punkte) und an MUI zu themen bei Echtzeit-Updates.'
      ]
    },
    shipped: {
      en: [
        'React SPA with Redux and real-time sockets for live workspace updates',
        'Plotly.js integration with MUI theme and performance tuning for large datasets',
        'Remote 3D/graphical view, forms, logs, and scripting in one UI'
      ],
      de: [
        'React SPA mit Redux und Echtzeit-Sockets für Live-Arbeitsbereich-Updates',
        'Plotly.js-Integration mit MUI-Theme und Performance-Tuning für große Datensätze',
        'Remote-3D-/Grafikansicht, Formulare, Logs und Skripting in einer UI'
      ]
    },
    proof: {
      en: [
        'Real-time data processing and visualization at scale',
        'Collaborative workspace with live updates; responsive desktop/mobile'
      ],
      de: [
        'Echtzeit-Datenverarbeitung und -visualisierung im Maßstab',
        'Kollaborativer Arbeitsbereich mit Live-Updates; responsive Desktop/Mobil'
      ]
    },
    stackGrouped: {
      en: 'React + TS • Redux • WebSockets / Socket.IO • MUI • Plotly.js • Jest',
      de: 'React + TS • Redux • WebSockets / Socket.IO • MUI • Plotly.js • Jest'
    },
    nicheTieIn: {
      en: 'Scientific web client with real-time rendering and heavy data viz.'
    },
    tags: ['Scientific Tools', 'Data Visualization', 'Platforms'],
    features: {
      en: [
        'Real-time data processing and visualization',
        'Collaborative workspace with live updates',
        'Secure authentication and role-based access control',
        'Responsive design for desktop and mobile use'
      ],
      de: [
        'Real-time Datenverarbeitung und Visualisierung',
        'Zusammenarbeit im Arbeitsbereich mit Live-Updates',
        'Sichere Authentifizierung und rollenbasierte Zugriffssteuerung',
        'Responsive Design für Desktop- und Mobilgeräte'
      ]
    },
    links: {
      demo: 'https://sim4life.swiss'
    },
    secondaryImages: [
      '/assets/Sim4Life_02.png',
      '/assets/Sim4Life_03.png'
    ],
    codeSnippets: [
      {
        language: 'typescript',
        code: `// src/plot/Plot.js (excerpt)
const Plot = React.forwardRef((props, ref) => {
  // ... existing code ...
  const customLayout = useMemo(() => {
    const ret = merge({
      paper_bgcolor: theme.palette.background.paper,
      plot_bgcolor: theme.palette.background.paper,
      // ... axis and legend theming ...
      autosize: true,
      margin: { t: 24, b: 28, l: 24, r: 24 },
      hoverlabel: {
        bgcolor: theme.palette.background.paper,
        font: { color: theme.palette.text.primary }
      },
      // ... 3D scene theming ...
    }, layout);
    delete ret.title.text
    return ret
  }, [layout, theme])

  const customData = useMemo(() => {
    return data.map(plotData => merge({
      colorbar: { tickfont: { color: theme.palette.text.primary } },
      textfont: { color: theme.palette.text.primary }
    }, plotData))
  }, [data, theme])

  if (customData.some(plot => (plot.x && plot.x.length > 65536) || (plot.y && plot.y.length > 65536))) {
    // Disable hover ticks for heavy plots for performance reasons
    customLayout.hovermode = false
  }

  return (
    <>
      <div style={{ height: '100%', width: '100%', paddingTop: '5px' }}>
        <PlotlyPlot
          ref={ref}
          layout={customLayout}
          data={customData}
          config={{ displaylogo: false }}
          style={{ height: '100%', width: '100%' }}
          useResizeHandler={true}
          {...rest}
        />
        {/* ...edit button and controls... */}
      </div>
      {/* ...plot controls... */}
    </>
  )
});`,
        info: {
          en: `Why it matters:
• Integrates Plotly.js for advanced visualisation in React.
• Adapts plot theming dynamically to the app's Material-UI theme.
• Improves usability on large datasets by reducing hover interactions.
• Supports interactive plot editing with custom controls.`,
          de: `Warum es relevant ist:
• Integriert Plotly.js für anspruchsvolle Visualisierungen in React.
• Passt das Plot-Theming dynamisch an das Material-UI-Theme der App an.
• Verbessert die Bedienbarkeit bei grossen Datensätzen, indem Hover-Interaktionen reduziert werden.
• Unterstützt interaktive Plotbearbeitung mit eigenen Steuerelementen.`
        }
      }
    ]
  },
  {
    id: 'o2sparc',
    title: {
      en: 'o²S²PARC',
      de: 'o²S²PARC'
    },
    company: {
      en: 'SPEAG',
      de: 'SPEAG'
    },
    appType: 'web',
    description: {
      en: 'Frontend work on a collaborative simulation environment for computational science, with a focus on UI consistency, component behaviour, and responsive interaction.',
      de: 'Frontend-Arbeit an einer kollaborativen Simulationsumgebung für computergestützte Wissenschaft, mit Fokus auf UI-Konsistenz, Komponentenverhalten und responsive Interaktion.'
    },
    year: '2023',
    role: {
      en: 'Frontend Engineer',
      de: 'Frontend-Ingenieur'
    },
    stack: ['Qooxdoo Framework', 'Frontend Apps', 'WebSocket'],
    imageUrl: '/assets/o2sparc_01.png',
    summary: {
      en: 'Researchers and clinicians use a web workspace for simulation workflows, studies, data management, and collaboration. My work focused on frontend behaviour, consistency, and maintaining complex UI flows within a large Qooxdoo codebase.',
      de: 'Forscher und Kliniker nutzen einen Web-Workspace für Simulations-Workflows, Studien, Datenverwaltung und Zusammenarbeit. Meine Arbeit konzentrierte sich auf Frontend-Verhalten, Konsistenz und die Pflege komplexer UI-Abläufe in einer grossen Qooxdoo-Codebasis.'
    },
    outcomeHeadline: {
      en: 'Frontend improvements for a complex scientific platform with rich widgets, shared state, and real-time updates.',
      de: 'Frontend-Verbesserungen für eine komplexe wissenschaftliche Plattform mit umfangreichen Widgets, gemeinsamem Zustand und Echtzeit-Updates.'
    },
    hardParts: {
      en: [
        'Maintaining complex UI behaviour in a large Qooxdoo codebase with rich widgets, shared state, and real-time updates.'
      ],
      de: [
        'Komplexes UI-Verhalten in einer grossen Qooxdoo-Codebasis mit umfangreichen Widgets, gemeinsamem Zustand und Echtzeit-Updates zu pflegen.'
      ]
    },
    shipped: {
      en: [
        'Qooxdoo frontend work with store patterns, property bindings, and UI component updates',
        'UI improvements for workbench views, status indicators, notifications, and responsive behaviour',
        'Maintenance of complex scientific platform flows within an existing product ecosystem'
      ],
      de: [
        'Qooxdoo-Frontend-Arbeit mit Store-Patterns, Property Bindings und UI-Komponenten-Updates',
        'UI-Verbesserungen für Workbench-Ansichten, Statusanzeigen, Benachrichtigungen und responsives Verhalten',
        'Pflege komplexer wissenschaftlicher Plattform-Flows innerhalb eines bestehenden Produkt-Ökosystems'
      ]
    },
    proof: {
      en: [
        'Complex scientific platform UI with shared state and real-time updates',
        'Work on rich widgets, product variants, and existing enterprise-scale frontend patterns'
      ],
      de: [
        'Komplexe wissenschaftliche Plattform-UI mit gemeinsamem Zustand und Echtzeit-Updates',
        'Arbeit an umfangreichen Widgets, Produktvarianten und bestehenden Frontend-Patterns auf Enterprise-Niveau'
      ]
    },
    stackGrouped: {
      en: 'Qooxdoo • TypeScript • WebSocket / socket.io • VirtualTree • Plotly / Three.js',
      de: 'Qooxdoo • TypeScript • WebSocket / socket.io • VirtualTree • Plotly / Three.js'
    },
    nicheTieIn: {
      en: 'Scientific platform UI work with complex state, performance-sensitive views, and collaboration workflows.',
      de: 'UI-Arbeit an einer wissenschaftlichen Plattform mit komplexem Zustand, performance-sensitiven Ansichten und Kollaborations-Workflows.'
    },
    tags: ['Scientific Tools', 'Platforms', 'Data Visualization'],
    features: {
      en: [
        'Collaborative web workspace for computational science workflows',
        'UI consistency and component behaviour improvements',
        'Workbench views, status indicators, notifications and responsive interaction',
        'Frontend maintenance within a large Qooxdoo-based platform'
      ],
      de: [
        'Kollaborativer Web-Workspace für computergestützte Wissenschafts-Workflows',
        'Verbesserungen an UI-Konsistenz und Komponentenverhalten',
        'Workbench-Ansichten, Statusanzeigen, Benachrichtigungen und responsive Interaktion',
        'Frontend-Pflege innerhalb einer grossen Qooxdoo-basierten Plattform'
      ]
    },
    links: {
      github: 'https://github.com/ITISFoundation/osparc-simcore',
    },
    secondaryImages: [
      '/assets/o2sparc_02.png',
      '/assets/o2sparc_03.png'
    ],
    codeSnippets: [
      {
        language: 'typescript',
        code: `__getIndicatorColor: function(freeSpace) {
  const warningSize = osparc.utils.Utils.gBToBytes(this.__lowDiskThreshold); // 5 GB Default
  const criticalSize = osparc.utils.Utils.gBToBytes(0.01); // 0 GB
  let color = qx.theme.manager.Color.getInstance().resolve("success");

  if (freeSpace <= criticalSize) {
    color = qx.theme.manager.Color.getInstance().resolve("error")
  } else if (freeSpace <= warningSize) {
    color = qx.theme.manager.Color.getInstance().resolve("warning")
  } else {
    color = qx.theme.manager.Color.getInstance().resolve("success")
  }
  return color
}`,
        info: {
          en: `This snippet shows a small but practical UI decision: mapping disk space to a visual status.

Why it matters:
• It turns a technical backend value into a clear user-facing signal.
• It uses configurable thresholds rather than hard-coded UI behaviour.
• It integrates with the Qooxdoo theme system instead of using raw colours.
• It keeps warning and critical states visually consistent with the rest of the product.

It is a small example of the kind of frontend detail that makes complex tools easier to understand.`,
          de: `Dieser Ausschnitt zeigt eine kleine, aber praktische UI-Entscheidung: freier Speicherplatz wird in einen visuellen Status übersetzt.

Warum das relevant ist:
• Ein technischer Backend-Wert wird zu einem klaren Signal für Nutzer.
• Die Schwellenwerte sind konfigurierbar statt fest in der UI verdrahtet.
• Die Farben kommen aus dem Qooxdoo-Theme-System statt aus rohen Farbwerten.
• Warn- und Fehlerzustände bleiben visuell konsistent mit dem restlichen Produkt.

Es ist ein kleines Beispiel dafür, wie Frontend-Details komplexe Werkzeuge verständlicher machen.`
        }
      }
    ]
  },
  {
    id: 'feeting',
    title: {
      en: 'Feeting',
      de: 'Feeting'
    },
    company: {
      en: 'Talent Maps AG',
      de: 'Talent Maps AG'
    },
    appType: 'cross-platform',
    description: {
      en: 'A mobile and web product for walking meetings, designed around audio-first, hands-free conversations and post-meeting follow-up.',
      de: 'Ein Mobile- und Web-Produkt für Walking Meetings, gestaltet für audio-first, freihändige Gespräche und Nachbereitung nach dem Meeting.'
    },
    year: '2022',
    role: {
      en: 'Frontend Engineer',
      de: 'Frontend-Ingenieur'
    },
    stack: ['React Native', 'React', 'Redux', 'React Navigation', 'Firebase', 'Auth0', 'Sentry', 'AppCenter', 'Pusher', 'i18n-js', 'React Native Paper', 'CodePush'],
    imageUrl: '/assets/Feeting_01.png',
    summary: {
      en: 'Users could host or join live audio meetings via share links, take part in calls, and receive post-call insights and transcripts. The product combined real-time communication, session state, presence, and production monitoring.',
      de: 'Nutzer konnten Live-Audio-Meetings über Share-Links starten oder beitreten, an Gesprächen teilnehmen und nach dem Call Insights und Transkripte erhalten. Das Produkt verband Echtzeitkommunikation, Session-State, Presence und Produktions-Monitoring.'
    },
    outcomeHeadline: {
      en: 'Walking-meeting product with live audio, presence, deep links, and post-call insights.',
      de: 'Walking-Meeting-Produkt mit Live-Audio, Presence, Deep Links und Post-Call-Insights.'
    },
    hardParts: {
      en: [
        'Keeping real-time call and presence state in sync across navigation stacks and varying network conditions.'
      ],
      de: [
        'Echtzeit-Anruf- und Presence-State über Navigations-Stacks und wechselnde Netzwerkbedingungen synchron zu halten.'
      ]
    },
    shipped: {
      en: [
        'Feature-based RN app with Redux for auth and selected feeting; React Navigation stacks/tabs and deep links',
        'Agora for live audio; Pusher for presence; Firebase + local handlers for push',
        'Shared theme and React Native Paper; Sentry, AppCenter, Heap for stability and analytics'
      ],
      de: [
        'Feature-basierte RN-App mit Redux für Auth und ausgewähltes Feeting; React Navigation Stacks/Tabs und Deep Links',
        'Agora für Live-Audio; Pusher für Presence; Firebase + lokale Handler für Push',
        'Gemeinsames Theme und React Native Paper; Sentry, AppCenter, Heap für Stabilität und Analytics'
      ]
    },
    proof: {
      en: [
        'Production-ready real-time stack (Agora + Pusher + push) and analytics/monitoring',
        'Deep-link handling and onboarding flow; consistent UI via shared theme'
      ],
      de: [
        'Produktionsreifer Echtzeit-Stack (Agora + Pusher + Push) und Analytics/Monitoring',
        'Deep-Link-Handling und Onboarding-Flow; konsistente UI über gemeinsames Theme'
      ]
    },
    stackGrouped: {
      en: 'React Native + TS • Redux • React Navigation • Agora • Pusher • Auth0 • Sentry / AppCenter',
      de: 'React Native + TS • Redux • React Navigation • Agora • Pusher • Auth0 • Sentry / AppCenter'
    },
    nicheTieIn: {
      en: 'Real-time mobile product work with audio calls, presence, deep links, and production reliability.',
      de: 'Echtzeit-Mobile-Produktarbeit mit Audio-Anrufen, Presence, Deep Links und Produktionszuverlässigkeit.'
    },
    tags: ['Real-time Mobile', 'Platforms'],
    features: {
      en: [
        'Live audio meetings with share-link access',
        'Post-call insights and meeting transcripts',
        'Presence and real-time meeting status updates via Pusher',
        'Calendar integration with Outlook and Google Calendar',
        'Cross-platform push notifications',
        'Health integration with Apple Health and Google Fit',
        'Error tracking, analytics and crash reporting with Sentry and AppCenter',
        'Over-the-air updates with CodePush',
        'Multi-language support with i18n-js',
        'Shared UI components and theming with React Native Paper'
      ],
      de: [
        'Live-Audio-Meetings mit Zugang über Share-Link',
        'Post-Call-Insights und Meeting-Transkripte',
        'Presence und Echtzeit-Meetingstatus über Pusher',
        'Kalenderintegration mit Outlook und Google Calendar',
        'Plattformübergreifende Push-Benachrichtigungen',
        'Integration mit Apple Health und Google Fit',
        'Error Tracking, Analytics und Crash Reporting mit Sentry und AppCenter',
        'Over-the-Air-Updates mit CodePush',
        'Mehrsprachigkeit mit i18n-js',
        'Gemeinsame UI-Komponenten und Theming mit React Native Paper'
      ]
    },
    secondaryImages: [
      '/assets/Feeting_03.png',
      '/assets/Feeting_04.png',
      '/assets/Feeting_02.png',
      '/assets/Feeting_05.png'
    ],
    codeSnippets: [
      {
        language: 'typescript',
        code: `// Example of Feeting Gems generation
const generateMeetingGems = async (transcript: string) => {
  const analysis = await ai.analyzeTranscript(transcript);
  return {
    topics: analysis.topics,
    actions: analysis.actionItems,
    questions: analysis.questions,
    sentiment: analysis.sentiment,
    summary: await ai.generateSummary(transcript)
  };
};`,
        info: {
          en: `This snippet illustrates the idea behind Feeting Gems: turning meeting transcripts into structured follow-up information.

Why it matters:
• Meetings become easier to review after the call.
• Topics, actions and questions can be surfaced from the transcript.
• The product value continues after the live audio meeting ends.
• It connects real-time communication with practical post-meeting workflows.

For the portfolio, this is less about the exact implementation and more about the product pattern: helping users keep useful context after a conversation.`,
          de: `Dieser Ausschnitt zeigt die Idee hinter Feeting Gems: Meeting-Transkripte werden in strukturierte Nachbereitungsinformationen übersetzt.

Warum das relevant ist:
• Meetings lassen sich nach dem Call leichter nachbereiten.
• Themen, Aufgaben und Fragen können aus dem Transkript sichtbar gemacht werden.
• Der Produktwert endet nicht mit dem Live-Audio-Meeting.
• Echtzeitkommunikation wird mit praktischen Follow-up-Workflows verbunden.

Für das Portfolio geht es hier weniger um die exakte Implementierung, sondern um das Produktmuster: hilfreichen Kontext nach einem Gespräch nutzbar zu machen.`
        }
      }
    ]
  },
  {
    id: 'coffeecall',
    title: {
      en: 'CoffeeCall',
      de: 'CoffeeCall'
    },
    company: {
      en: 'Talent Maps AG',
      de: 'Talent Maps AG'
    },
    appType: 'mobile',
    description: {
      en: 'A mobile app for spontaneous and scheduled coffee chats, networking, and real-time calls.',
      de: 'Eine Mobile-App für spontane und geplante Coffee Chats, Networking und Echtzeit-Anrufe.'
    },
    year: '2021',
    role: {
      en: 'Frontend Engineer',
      de: 'Frontend-Ingenieur'
    },
    stack: [
      'React Native',
      'React',
      'Redux',
      'TypeScript',
      'Jest',
      'ESLint',
      'Agora SDK',
      'App Center',
      'Sentry',
      'LaunchDarkly'
    ],
    imageUrl: '/assets/CoffeeCall_01.png',
    summary: {
      en: 'Employees and communities could arrange 1:1 or group coffee chats with availability, groups, favourites, and real-time calling. The app combined React Native, call state, native call behaviour, monitoring, and over-the-air updates.',
      de: 'Mitarbeiter und Communities konnten 1:1- oder Gruppen-Coffee-Chats mit Verfügbarkeit, Gruppen, Favoriten und Echtzeit-Anrufen organisieren. Die App verband React Native, Call-State, natives Anrufverhalten, Monitoring und Over-the-Air-Updates.'
    },
    outcomeHeadline: {
      en: 'Mobile coffee-chat app with real-time calls, groups, native call behaviour, monitoring, and OTA updates.',
      de: 'Mobile Coffee-Chat-App mit Echtzeit-Anrufen, Gruppen, nativem Anrufverhalten, Monitoring und OTA-Updates.'
    },
    hardParts: {
      en: [
        'Integrating native call UI and VoIP pushes for reliable incoming-call behaviour across iOS and Android.'
      ],
      de: [
        'Integration nativer Anruf-UI und VoIP-Push für zuverlässiges eingehendes Anrufverhalten auf iOS und Android.'
      ]
    },
    shipped: {
      en: [
        'React Native app with Redux for user/session and call state; React Navigation stack + tabs and auth gating',
        'Agora for low-latency calls; CallKeep + VoIP push for native incoming-call UX',
        'Sentry + CodePush + AppCenter for monitoring and fast OTA fixes; Auth0, i18n'
      ],
      de: [
        'React Native App mit Redux für User/Session- und Anrufzustand; React Navigation Stack + Tabs und Auth-Gating',
        'Agora für Low-Latency-Anrufe; CallKeep + VoIP-Push für native eingehende Anruf-UX',
        'Sentry + CodePush + AppCenter für Monitoring und schnelle OTA-Fixes; Auth0, i18n'
      ]
    },
    proof: {
      en: [
        'Production monitoring (Sentry, AppCenter) and fast fixes via CodePush',
        'Real-time calling and push stack; internationalization and shared style system'
      ],
      de: [
        'Produktions-Monitoring (Sentry, AppCenter) und schnelle Fixes via CodePush',
        'Echtzeit-Anrufe und Push-Stack; Internationalisierung und gemeinsames Style-System'
      ]
    },
    stackGrouped: {
      en: 'React Native + TS • Redux • Agora • CallKeep / VoIP • Auth0 • Sentry / CodePush / AppCenter',
      de: 'React Native + TS • Redux • Agora • CallKeep / VoIP • Auth0 • Sentry / CodePush / AppCenter'
    },
    nicheTieIn: {
      en: 'Real-time mobile app work with native call behaviour and production reliability.',
      de: 'Echtzeit-Mobile-App-Arbeit mit nativem Anrufverhalten und Produktionszuverlässigkeit.'
    },
    tags: ['Real-time Mobile'],
    features: {
      en: [
        'User authentication and profile/session management',
        '1:1 and group coffee-chat flows',
        'Real-time calling via Agora SDK',
        'Native incoming-call behaviour with CallKeep and VoIP push handling',
        'Groups, favourites, availability and meeting history',
        'Internationalisation support',
        'Error tracking and crash reporting with Sentry and App Center',
        'Over-the-air updates with CodePush',
        'Feature flag support with LaunchDarkly'
      ],
      de: [
        'Authentifizierung und Profil-/Session-Management',
        '1:1- und Gruppen-Flows für Coffee Chats',
        'Echtzeit-Anrufe über Agora SDK',
        'Natives Verhalten für eingehende Anrufe mit CallKeep und VoIP-Push-Handling',
        'Gruppen, Favoriten, Verfügbarkeit und Meeting-Historie',
        'Internationalisierung',
        'Error Tracking und Crash Reporting mit Sentry und App Center',
        'Over-the-Air-Updates mit CodePush',
        'Feature-Flag-Support mit LaunchDarkly'
      ]
    },
    secondaryImages: [
      '/assets/CoffeeCall_02.png',
      '/assets/CoffeeCall_03.png',
      '/assets/CoffeeCall_04.png'
    ],
    codeSnippets: [
      {
        language: 'typescript',
        code: `function Animation({ origin, soundPath, animationPath }: AnimationProps): ReactElement {
    const animation = useRef<LottieView>(null);
    const sound = useRef<Video>(null);
    const [isPlaying, setIsPLaying] = useState<boolean>(false);
    const playAnimation = () => animation?.current?.play();
    const stopAnimation = () => animation?.current?.pause();

    useFocusEffect(
        useCallback(() => {
            setIsPLaying(true);
            setTimeout(() => {
                playAnimation();
            }, 300);
            return () => {
                setIsPLaying(false);
                stopAnimation();
            };
        }, []),
    );

    return (
        <View style={...}>
            <LottieView
                ref={animation}
                source={animationPath}
                // ...other props
            />
            <Video
                paused={!isPlaying}
                ref={sound}
                source={soundPath}
                audioOnly
                repeat
            />
        </View>
    );
}`,
        info: {
          en: `This snippet shows a React Native component that combines animation and audio playback.

Why it matters:
• Lottie animation and audio are combined into a richer mobile interaction.
• useFocusEffect controls the lifecycle when the screen gains or loses focus.
• Refs give direct control over animation and media playback.
• Cleanup prevents animation or audio from continuing in the background.

The component demonstrates common React Native concerns: lifecycle handling, media control, reuse, and cross-platform behaviour.`,
          de: `Dieser Ausschnitt zeigt eine React-Native-Komponente, die Animation und Audio-Wiedergabe miteinander verbindet.

Warum das relevant ist:
• Lottie-Animationen und Audio werden für eine interaktive mobile Erfahrung kombiniert.
• useFocusEffect steuert den Lebenszyklus beim Betreten und Verlassen des Screens.
• Refs ermöglichen direkte Kontrolle über Animation und Medienwiedergabe.
• Cleanup verhindert, dass Animation oder Audio im Hintergrund weiterlaufen.

Die Komponente zeigt typische React-Native-Themen: Lifecycle-Handling, Mediensteuerung, Wiederverwendbarkeit und plattformübergreifendes Verhalten.`
        }
      }
    ]
  },
  // {
  //   id: 'version-info-npm',
  //   title: {
  //     en: '@julian-querido/version-info',
  //     de: '@julian-querido/version-info'
  //   },
  //   company: {
  //     en: 'Open Source',
  //     de: 'Open Source'
  //   },
  //   appType: 'web',
  //   description: {
  //     en: 'A simple, zero-config version info display for Vite/React apps. Shows version, environment, commit hash, and build time from your build pipeline.',
  //     de: 'Ein einfaches, konfigurationsfreies Version-Info-Display für Vite/React-Apps. Zeigt Version, Umgebung, Commit-Hash und Build-Zeit aus der Build-Pipeline an.'
  //   },
  //   year: '2025',
  //   role: {
  //     en: 'Author & Maintainer',
  //     de: 'Autor & Maintainer'
  //   },
  //   imageUrl: '/assets/Versioninfo_01.png',
  //   stack: ['TypeScript', 'React', 'Vite', 'NPM', 'Open Source'],
  //   summary: {
  //     en: 'The @julian-querido/version-info package provides a plug-and-play React component to display build and version information in your app, making it easy to surface version, environment, commit, and build time for debugging and transparency.',
  //     de: 'Das @julian-querido/version-info-Paket bietet eine Plug-and-Play-React-Komponente zur Anzeige von Build- und Versionsinformationen in Ihrer App. Es macht es einfach, Version, Umgebung, Commit und Build-Zeit für Debugging und Transparenz anzuzeigen.'
  //   },
  //   outcomeHeadline: {
  //     en: 'Ship build and version info in Vite/React apps without custom wiring—plug-and-play, zero config.'
  //   },
  //   stackGrouped: {
  //     en: 'TypeScript • React • Vite • NPM'
  //   },
  //   tags: ['Open Source'],
  //   features: {
  //     en: [
  //       'Zero-config: Just install and use',
  //       'Displays version, environment, commit hash, and build time',
  //       'Works out-of-the-box with Vite',
  //       'Customizable display component',
  //       'Great for debugging and transparency',
  //       'Open source and MIT licensed'
  //     ],
  //     de: [
  //       'Keine Konfiguration: Einfach installieren und verwenden',
  //       'Zeigt Version, Umgebung, Commit-Hash und Build-Zeit an',
  //       'Funktioniert sofort mit Vite',
  //       'Anpassbare Anzeige-Komponente',
  //       'Ideal für Debugging und Transparenz',
  //       'Open Source und MIT-lizenziert'
  //     ]
  //   },
  //   links: {
  //     github: 'https://github.com/jsaq007/version-info',
  //     demo: 'https://www.npmjs.com/package/@julian-querido/version-info'
  //   }
  // }
  //   {
  //     id: 'gigglebunch',
  //     title: {
  //       en: 'GiggleBunch',
  //       de: 'GiggleBunch'
  //     },
  //     company: {
  //       en: 'Personal Project',
  //       de: 'Persönliches Projekt'
  //     },
  //     appType: 'cross-platform',
  //     description: {
  //       en: 'An interactive children\'s story app featuring characters like Mango Cat, Orange Panda, and their jungle friends with immersive storytelling experiences',
  //       de: 'Eine interaktive Kinder-Geschichten-App mit Charakteren wie Mango Cat, Orange Panda und ihren Dschungelfreunden mit immersiven Geschichtenerzählungen'
  //     },
  //     year: '2025',
  //     role: {
  //       en: 'Full Stack Developer',
  //       de: 'Full Stack Entwickler'
  //     },
  //     stack: [
  //       'React Native',
  //       'Expo',
  //       'Expo Router',
  //       'Zustand',
  //       'TypeScript',
  //       'Expo AV (Audio/Video)',
  //       'i18next & React i18next',
  //       'Expo Haptics',
  //       'Expo Screen Orientation',
  //       'Expo Localization',
  //       'React Native Reanimated',
  //       'React Native Gesture Handler',
  //       'Expo Linear Gradient',
  //       'Expo Blur',
  //       'Expo Image',
  //       'Expo Secure Store',
  //       'React Native Async Storage',
  //       'React Native Sound',
  //       'React Native TTS'
  //     ],
  //     imageUrl: '/assets/project_fallback.png',
  //     summary: {
  //       en: 'GiggleBunch is an interactive children\'s story app featuring animated characters like Mango Cat, Orange Panda, and their jungle friends. The app provides immersive storytelling experiences with audio narration, beautiful illustrations, and branching narratives that adapt to user choices. It supports multiple users, multi-language content, and cross-platform deployment.',
  //       de: 'GiggleBunch ist eine interaktive Kinder-Geschichten-App mit animierten Charakteren wie Mango Cat, Orange Panda und ihren Dschungelfreunden. Die App bietet immersive Geschichtenerzählungen mit Audio-Narration, wunderschönen Illustrationen und verzweigten Erzählungen, die sich an Benutzerentscheidungen anpassen. Sie unterstützt mehrere Benutzer, mehrsprachige Inhalte und Cross-Platform-Deployment.'
  //     },
  //     features: {
  //       en: [
  //         'Multi-user profiles with individual language preferences (English/German)',
  //         'Character-based story organization (Mango Cat, Orange Panda, GiggleBunch)',
  //         'Interactive book-style reading with left/right page spreads',
  //         'Synchronized audio narration with background music',
  //         'Branching narratives that adapt to user choices',
  //         'Dynamic content discovery with automatic story scanning',
  //         'Cross-platform support (iOS, Android, Web)',
  //         'Responsive design adapting to portrait/landscape orientations',
  //         'iOS Dynamic Island integration for audio controls',
  //         'Progress tracking and automatic story completion saving',
  //         'Hand-drawn illustrations and beautiful character artwork',
  //         'Theme support with light/dark mode compatibility',
  //         'Audio focus management for external audio apps',
  //         'Haptic feedback for enhanced user interaction',
  //         'Zero-configuration content addition via filesystem scanning'
  //       ],
  //       de: [
  //         'Multi-Benutzer-Profile mit individuellen Sprachpräferenzen (Englisch/Deutsch)',
  //         'Charakterbasierte Geschichtenorganisation (Mango Cat, Orange Panda, GiggleBunch)',
  //         'Interaktives buchartiges Lesen mit linken/rechten Seitenaufschlägen',
  //         'Synchronisierte Audio-Narration mit Hintergrundmusik',
  //         'Verzweigte Erzählungen, die sich an Benutzerentscheidungen anpassen',
  //         'Dynamische Inhaltserkennung mit automatischer Geschichtensuche',
  //         'Cross-Platform-Unterstützung (iOS, Android, Web)',
  //         'Responsive Design, das sich an Portrait/Landscape-Orientierungen anpasst',
  //         'iOS Dynamic Island-Integration für Audio-Steuerungen',
  //         'Fortschrittsverfolgung und automatisches Speichern der Geschichtenabschlüsse',
  //         'Handgezeichnete Illustrationen und wunderschöne Charakterkunst',
  //         'Theme-Unterstützung mit Hell/Dunkel-Modus-Kompatibilität',
  //         'Audio-Fokus-Management für externe Audio-Apps',
  //         'Haptisches Feedback für verbesserte Benutzerinteraktion',
  //         'Konfigurationsfreie Inhaltsergänzung über Dateisystem-Scanning'
  //       ]
  //     },
  //     links: {
  //       github: 'https://github.com/jsaq007/gigglebunch'
  //     },
  //     codeSnippets: [
  //       {
  //         language: 'typescript',
  //         code: `// Dynamic content discovery system
  // const useStoryDiscovery = () => {
  //   const [stories, setStories] = useState<Story[]>([]);
  //   const [characters, setCharacters] = useState<Character[]>([]);

  //   useEffect(() => {
  //     const discoverContent = async () => {
  //       try {
  //         // Scan filesystem for new stories and characters
  //         const storyFiles = await FileSystem.readDirectoryAsync(storiesPath);
  //         const characterFiles = await FileSystem.readDirectoryAsync(charactersPath);

  //         // Validate and load content dynamically
  //         const validStories = await Promise.all(
  //           storyFiles.map(async (file) => {
  //             const storyData = await FileSystem.readAsStringAsync(file);
  //             return validateStoryData(JSON.parse(storyData));
  //           })
  //         );

  //         setStories(validStories.filter(Boolean));
  //         setCharacters(await loadCharacters(characterFiles));
  //       } catch (error) {
  //         console.error('Content discovery failed:', error);
  //       }
  //     };

  //     discoverContent();
  //   }, []);

  //   return { stories, characters };
  // };`,
  //         info: {
  //           en: `This code snippet demonstrates the dynamic content discovery system in GiggleBunch. It's complex because it:

  // 1. Implements a zero-configuration content management system that automatically discovers new stories and characters
  // 2. Uses React Native's FileSystem API to scan directories for content files
  // 3. Validates story data integrity before loading into the app
  // 4. Handles asynchronous file operations with proper error handling
  // 5. Provides a clean hook interface for components to access discovered content
  // 6. Supports dynamic content updates without requiring app restarts

  // The system allows content creators to simply add story files to the app's directory structure, and the app will automatically discover and load them. This makes content management extremely simple and user-friendly.`,
  //           de: `Dieser Codeausschnitt zeigt das dynamische Inhaltserkennungssystem in GiggleBunch. Es ist komplex, weil es:

  // 1. Ein konfigurationsfreies Inhaltsverwaltungssystem implementiert, das automatisch neue Geschichten und Charaktere entdeckt
  // 2. Verwendet React Native's FileSystem API, um Verzeichnisse nach Inhaltsdateien zu durchsuchen
  // 3. Validiert die Integrität der Geschichtsdaten vor dem Laden in die App
  // 4. Verarbeitet asynchrone Dateioperationen mit ordnungsgemäßer Fehlerbehandlung
  // 5. Bietet eine saubere Hook-Schnittstelle für Komponenten, um auf entdeckte Inhalte zuzugreifen
  // 6. Unterstützt dynamische Inhaltsaktualisierungen ohne App-Neustarts

  // Das System ermöglicht es Inhaltserstellern, einfach Geschichtsdateien zur Verzeichnisstruktur der App hinzuzufügen, und die App wird sie automatisch entdecken und laden. Dies macht die Inhaltsverwaltung extrem einfach und benutzerfreundlich.`
  //         }
  //       }
  //     ]
  //   }
]; 